import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type CopyToClipboardCardProps = {
  label: string;
  value: string;
  displayValue?: string;
  disabled?: boolean;
  className?: string;
  onCopy?: () => void;
  children?: React.ReactNode;
};

export const CopyToClipboardCard = ({
  label,
  value,
  displayValue,
  disabled = false,
  className,
  onCopy,
  children,
}: CopyToClipboardCardProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 2000);

    onCopy?.();
  };

  return (
    <div
      className={cn(
        "rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm",
        className,
      )}
    >
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-gray-700 text-sm">{label}</p>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 p-3 border rounded-md bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <div
                className={cn(
                  "font-mono text-base truncate min-w-0 flex-1",
                  disabled && "blur-sm select-none",
                )}
              >
                {displayValue ?? value}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  disabled={disabled}
                  className="min-w-[80px]"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      {t("common.copied")}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      {t("common.copy")}
                    </>
                  )}
                </Button>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
